import { inputCssNoMy } from "../reuseables/native.comp";
import { useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";
import productCore from "../../core/product.core";
import { useTranslation } from "../../core/translation.core";
import { ProductSearchRst2 } from "../../types/product";
import CloseIcon from "../../icons/iconClose.comp";
import LoadingOvalCenterIcon from "../../icons/loadingOvalCenter.icon";
import localeCore from "../../core/locale.core";

interface ProductDetails {
  id: string;
  name: string;
  price: number;
  inventory: number;
  productType: string;
}

export default function ProductSearchInput({
  addProduct,
  showVariants = false,
  showParent = true,
  withInventories = false,
}: {
  addProduct: (
    productId: string,
    productDetails: { name: string; price: number; inventory: number; productType: string }
  ) => void;
  showVariants?: boolean;
  showParent?: boolean;
  withInventories?: boolean;
}) {
  const [searchText, setSearchText] = useState<string>("");
  const {
    data: products,
    isError,
    isLoading,
  } = useQuery({
    queryKey: [searchText],
    queryFn: async (context) => {
      let products = await productCore.searchProduct(context.queryKey[0], withInventories, !showParent);
      if (showVariants) {
        products = { ...products, list: products.list.flatMap((product) => [product, ...product.variations]) };
      }
      return products;
    },
    enabled: searchText !== "",
  });

  return (
    <>
      <SearchBox
        onSearch={(text) => {
          setSearchText(text);
        }}
        isLoading={isLoading}
      />
      {isLoading && <LoadingOvalCenterIcon />}
      {!isError && searchText !== "" && (
        <ProductList
          showParent={showParent}
          products={products!}
          addProduct={(product) => {
            setSearchText("");
            addProduct(product.id, {
              name: product.name,
              price: product.price,
              inventory: product.inventory,
              productType: product.productType,
            });
          }}
          onClose={() => {
            setSearchText("");
          }}
        />
      )}
    </>
  );
}

const SearchBox = ({ onSearch, isLoading }: { onSearch: (text: string) => void; isLoading: boolean }) => {
  const [searchText, setSearchText] = useState<string>("");
  const ref = useRef<HTMLInputElement>(null);
  const { t, msg } = useTranslation();

  function click() {
    onSearch(searchText);
    setSearchText("");

    if (ref.current) {
      ref.current.value = "";
    }
  }

  return (
    <div className="flex gap-4 items-center">
      <input
        id="product-search"
        ref={ref}
        className={`${inputCssNoMy} grow`}
        placeholder={t(msg.product.searchProduct)}
        type="text"
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            click();
          }
        }}
        onChange={(e) => {
          setSearchText(e.target.value);
        }}
      />

      {isLoading ? (
        <span className="animate-spin rounded-full h-6 w-6 border-b-2 border-her-dodgerblue mx-2"></span>
      ) : (
        <button
          id="search-inventory-product"
          className="bg-her-dodgerblue text-white px-4 py-1 rounded-md h-9"
          onClick={(e) => {
            e.preventDefault();
            click();
          }}
          type="button"
        >
          {t(msg.general.search)}
        </button>
      )}
    </div>
  );
};

const ProductList = ({
  products,
  addProduct,
  onClose,
  showParent,
}: {
  products: ProductSearchRst2;
  addProduct: (product: ProductDetails) => void;
  onClose: () => void;
  showParent?: boolean;
}) => {
  const { t, msg } = useTranslation();

  if (!products) return null;
  console.log(products);

  return (
    <div className="relative w-full  border rounded-2xl shadow-lg">
      <button type="button" className="absolute top-3 right-3 text-red-500 transition-colors" onClick={onClose}>
        <CloseIcon className="w-5 h-5" />
      </button>

      {/* Product list */}
      <ul id="product-search-rst" className="mt-8 py-2 pl-10 pr-8 h-[320px] overflow-y-auto">
        {products.list
          .filter((pr) => (showParent ? true : pr.productType !== "variationParent"))
          .map((p) => {
            const totalInventory = p.inventories?.reduce((r, i) => r + Number(i.count), 0);
            const productName = localeCore.getLocaleValueDefaultFallback(p.name);

            return (
              <li
                key={p.id}
                onClick={(e) => {
                  e.preventDefault();
                  addProduct({
                    id: p.id,
                    name: productName,
                    price: p.price,
                    inventory: totalInventory,
                    productType: p.productType,
                  });
                }}
                className="cursor-pointer border border-gray-100 hover:bg-gray-500/30 rounded-2xl px-3 py-2 transition-colors my-3"
                data-sku={p.sku}
              >
                <button id="product-select" className="flex flex-col text-left w-full">
                  <span className="font-medium">
                    {productName} <span className="text-sm">({p.sku})</span>
                  </span>
                  <span className="text-sm">
                    {t(msg.product.price)}: <span className="font-semibold">${p.price}</span>
                  </span>
                  <span className="text-x">
                    {t(msg.inventory.inventory)}: {totalInventory ?? 0}
                  </span>
                </button>
              </li>
            );
          })}
      </ul>
    </div>
  );
};
